import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Clock, Award, Globe, Play, Lock, Coins,
  BookOpen, ChevronRight, RotateCcw, BarChart2, ArrowUpRight,
  Crown, CheckCircle2, Flame, Zap, Loader2
} from 'lucide-react'

export interface AttemptData {
  attempts_count: number
  has_attempted: boolean
  latest_attempt_id?: number
  latest_status?: string
  latest_score?: number
  best_score?: number
  max_score?: number
  accuracy?: number
  completed_at?: string
}

export interface TestItem {
  id: number
  title: string
  description: string | null
  test_type: string
  language: string
  duration_minutes: number
  question_count: number
  total_marks: number
  is_free: boolean
  credit_cost: number
  has_access: boolean
  unlocked?: boolean
  created_at?: string
  attempt_data?: AttemptData
  exam_name?: string
  syllabus_tree?: any
}

interface TestCardProps {
  test: TestItem
  isHot?: boolean
  isNew?: boolean
  onViewSyllabus?: (test: TestItem) => void
}

/**
 * Formats duration cleanly to prevent bugs like "TIME: m".
 */
export function formatTestDuration(minutes?: number | null): { text: string; isUntimed: boolean } {
  if (minutes === null || minutes === undefined || minutes <= 0) {
    return { text: 'Untimed', isUntimed: true }
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    return { text: `${Math.floor(minutes / 60)}h`, isUntimed: false }
  }
  if (minutes >= 60) {
    return { text: `${Math.floor(minutes / 60)}h ${minutes % 60}m`, isUntimed: false }
  }
  return { text: `${minutes}m`, isUntimed: false }
}

/**
 * Strips redundant repeated exam names from display titles.
 */
export function sanitizeDisplayTitle(title: string, examName?: string): string {
  if (!title) return ''
  if (examName) {
    const baseExam = examName.replace(/\s*\b(19|20)\d{2}\b/g, '').trim()
    if (baseExam.length >= 3) {
      const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const pattern = new RegExp(`(${escape(examName)}|${escape(baseExam)})\\s+(${escape(examName)}|${escape(baseExam)})`, 'i')
      if (pattern.test(title)) {
        return title.replace(pattern, '$1')
      }
    }
  }
  return title
}

/**
 * Formats test type into clean title case.
 */
function formatTestType(type: string): string {
  if (!type) return 'Mock'
  return type
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

export function TestCard({ test, isHot = false, isNew = false, onViewSyllabus }: TestCardProps) {
  const [unlocking, setUnlocking] = useState(false)
  const isUnlocked = test.has_access || test.is_free
  const isPremium = !isUnlocked
  const attemptData = test.attempt_data
  const hasAttempted = Boolean(attemptData?.has_attempted && attemptData.attempts_count > 0)
  const duration = formatTestDuration(test.duration_minutes)
  const displayTitle = sanitizeDisplayTitle(test.title, test.exam_name)

  const handleUnlock = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (unlocking) return

    setUnlocking(true)
    router.post(`/student/tests/${test.id}/purchase`, {}, {
      preserveScroll: true,
      onFinish: () => setUnlocking(false)
    })
  }

  return (
    <Card className={`transition-all duration-200 rounded-2xl flex flex-col justify-between group overflow-hidden relative border hover:-translate-y-0.5 ${
      isPremium
        ? 'border-tertiary/40 hover:border-tertiary/70 bg-card hover:shadow-glass-md'
        : hasAttempted
        ? 'border-border/70 hover:border-secondary/50 bg-card hover:shadow-glass-sm'
        : 'border-border/80 hover:border-primary/50 bg-card hover:shadow-glass-md'
    }`}>
      {/* Top Accent Strip for Premium tests */}
      {isPremium && (
        <div className="h-1 w-full bg-tertiary/70" />
      )}

      <CardContent className="p-3.5 sm:p-4 space-y-2.5 flex flex-col justify-between flex-1">
        {/* Header Badges */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            {/* Primary Status Badge */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {isPremium ? (
                <Badge className="font-extrabold text-[10px] sm:text-[11px] bg-tertiary/20 text-tertiary border border-tertiary/40 rounded-lg px-2 py-0.5 flex items-center gap-1 shadow-2xs">
                  <Crown className="h-3 w-3 text-tertiary" />
                  <span>PREMIUM MOCK</span>
                </Badge>
              ) : hasAttempted ? (
                <Badge className="font-extrabold text-[10px] bg-secondary/15 text-secondary border border-secondary/30 rounded-lg px-2 py-0.5 shadow-2xs flex items-center gap-1">
                  <RotateCcw className="h-3 w-3" />
                  <span>{attemptData?.attempts_count} Attempt{attemptData && attemptData.attempts_count > 1 ? 's' : ''}</span>
                </Badge>
              ) : (
                <Badge className="font-extrabold text-[10px] sm:text-[11px] bg-primary/15 text-primary border border-primary/30 rounded-lg px-2 py-0.5 flex items-center gap-1 shadow-2xs">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  <span>{test.is_free ? 'Free Mock' : 'Unlocked'}</span>
                </Badge>
              )}

              {/* Hot / New indicators */}
              {isHot && !hasAttempted && (
                <Badge className="font-black text-[9px] bg-destructive/15 text-destructive border border-destructive/30 rounded-md px-1.5 py-0.5 flex items-center gap-1 animate-pulse">
                  <Flame className="h-2.5 w-2.5 fill-current" />
                  <span>HOT</span>
                </Badge>
              )}
              {isNew && !hasAttempted && !isHot && (
                <Badge className="font-bold text-[9px] bg-primary/15 text-primary border border-primary/30 rounded-md px-1.5 py-0.5 flex items-center gap-1">
                  <Zap className="h-2.5 w-2.5" />
                  <span>NEW</span>
                </Badge>
              )}
            </div>

            {/* Test Type & Language */}
            <div className="flex items-center gap-1.5 shrink-0">
              {test.test_type && (
                <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground border-border/70 bg-muted/40 px-1.5 py-0.5 rounded-md">
                  {formatTestType(test.test_type)}
                </Badge>
              )}
              <Badge variant="outline" className="text-[9px] font-bold uppercase flex items-center gap-1 rounded-md text-muted-foreground border-border/70 bg-muted/40 px-1.5 py-0.5">
                <Globe className="h-2.5 w-2.5" />
                <span>{test.language?.toUpperCase() || 'EN'}</span>
              </Badge>
            </div>
          </div>

          {/* Exam Name Sub-header (if available) */}
          {test.exam_name && (
            <p className="text-[10px] font-bold text-primary tracking-wider uppercase">
              {test.exam_name}
            </p>
          )}

          {/* Title & Description */}
          <div className="space-y-0.5">
            <h3 className="text-sm sm:text-base font-bold leading-snug line-clamp-2">
              <Link
                href={`/student/tests/${test.id}`}
                className={`transition-colors hover:underline ${
                  isPremium ? 'text-foreground hover:text-tertiary' : 'text-foreground hover:text-primary'
                }`}
              >
                {displayTitle}
              </Link>
            </h3>
            <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {test.description || 'Comprehensive practice test covering core syllabus topics.'}
            </p>
          </div>

          {/* Premium Value Highlight */}
          {isPremium && (
            <div className="flex items-center gap-2 pt-0.5 text-xs text-tertiary">
              <span className="inline-flex items-center gap-1.5 bg-tertiary/15 px-2 py-0.5 rounded-lg border border-tertiary/30 font-semibold tracking-wide text-[10px]">
                <Award className="h-3 w-3 text-tertiary shrink-0" />
                <span>Detailed Solutions & Rank Prediction</span>
              </span>
            </div>
          )}
        </div>

        {/* Metadata Stats & Actions */}
        <div className="space-y-2 pt-2 border-t border-border/50">
          {/* Stats Matrix */}
          <div className="grid grid-cols-3 gap-1 bg-muted/40 p-1.5 rounded-xl border border-border/50 text-center">
            <div className="flex flex-col items-center justify-center">
              <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                <Clock className="h-3 w-3 text-primary shrink-0" />
                <span>Time</span>
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">
                {duration.text}
              </span>
            </div>

            <div className="flex flex-col items-center justify-center border-x border-border/50">
              <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                <BookOpen className="h-3 w-3 text-secondary shrink-0" />
                <span>Questions</span>
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">
                {test.question_count || 0} Qs
              </span>
            </div>

            <div className="flex flex-col items-center justify-center">
              <span className="text-[10px] font-bold text-muted-foreground uppercase flex items-center gap-1">
                <Award className="h-3 w-3 text-tertiary shrink-0" />
                <span>Marks</span>
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">
                {test.total_marks ?? (test.question_count ? test.question_count * 4 : 0)}
              </span>
            </div>
          </div>

          {/* Attempt Performance Strip (if attempted) */}
          {hasAttempted && (
            <div className="p-2 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 min-w-0">
                <BarChart2 className="h-3.5 w-3.5 text-secondary shrink-0" />
                <div className="truncate">
                  <span className="text-[10px] text-muted-foreground uppercase font-bold">Best: </span>
                  <span className="font-extrabold text-foreground">
                    {attemptData?.best_score ?? attemptData?.latest_score ?? 0} pts
                  </span>
                  {attemptData?.accuracy != null && (
                    <span className="text-muted-foreground ml-1 font-medium text-[10px]">
                      ({attemptData.accuracy}%)
                    </span>
                  )}
                </div>
              </div>

              {attemptData?.latest_attempt_id && (
                <Link
                  href={`/student/attempts/${attemptData.latest_attempt_id}/result`}
                  className="text-primary hover:underline font-bold text-[10px] inline-flex items-center gap-0.5 shrink-0 ml-1"
                >
                  <span>Analysis</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          )}

          {/* Action Row */}
          <div className="flex items-center gap-2 pt-0.5">
            {onViewSyllabus && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onViewSyllabus(test)
                }}
                className="h-8.5 px-2.5 rounded-xl border-border/80 bg-background/50 hover:bg-muted text-foreground text-xs font-bold gap-1 shrink-0 shadow-glass-xs hover:border-primary/50 transition-all cursor-pointer"
                title="View Test Syllabus & Topic Breakdown"
              >
                <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" />
                <span>Syllabus</span>
              </Button>
            )}

            {isUnlocked ? (
              <Button
                className={`flex-1 h-8.5 rounded-xl font-bold text-xs cursor-pointer transition-all shadow-2xs min-w-0 ${
                  hasAttempted
                    ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
                asChild
              >
                <Link href={`/student/tests/${test.id}`} className="px-2">
                  {hasAttempted ? (
                    <>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">Retake Test</span>
                    </>
                  ) : (
                    <>
                      <Play className="mr-1.5 h-3.5 w-3.5 fill-current shrink-0" />
                      <span className="truncate">Start Test</span>
                    </>
                  )}
                  <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-80 group-hover:translate-x-0.5 transition-transform shrink-0" />
                </Link>
              </Button>
            ) : (
              <Button
                type="button"
                disabled={unlocking}
                onClick={handleUnlock}
                className="flex-1 h-8.5 rounded-xl font-bold text-xs cursor-pointer transition-all shadow-2xs min-w-0 bg-tertiary text-tertiary-foreground hover:bg-tertiary/90 px-2"
              >
                {unlocking ? (
                  <div className="flex items-center justify-center gap-1.5 w-full">
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                    <span className="truncate">Unlocking...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full min-w-0 gap-1">
                    <span className="flex items-center gap-1 shrink-0 font-bold truncate">
                      <Lock className="h-3.5 w-3.5 shrink-0" /> Unlock
                    </span>
                    <span className="font-extrabold text-[10px] bg-background/20 text-tertiary-foreground rounded-md px-1.5 py-0.5 shrink-0 flex items-center gap-0.5">
                      <Coins className="h-2.5 w-2.5" />
                      {test.credit_cost} cr
                    </span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
